/**
 * MessageTemplateFormModal - Модальное окно создания/редактирования шаблона
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Alert,
  MultiSelect,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconTemplate,
  IconBrandWhatsapp,
  IconBrandTelegram,
  IconBrandVk,
  IconPhone,
  IconMail,
} from '@tabler/icons-react';

/**
 * Опции каналов
 */
const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp', icon: IconBrandWhatsapp },
  { value: 'telegram', label: 'Telegram', icon: IconBrandTelegram },
  { value: 'viber', label: 'Viber', icon: IconPhone },
  { value: 'vk', label: 'VK', icon: IconBrandVk },
  { value: 'email', label: 'Email', icon: IconMail },
];

/**
 * API функции
 */
const api = {
  async createTemplate(data) {
    const response = await fetch('/api/message-templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать шаблон');
    }
    return response.json();
  },

  async updateTemplate(id, data) {
    const response = await fetch(`/api/message-templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить шаблон');
    }
    return response.json();
  },
};

const MessageTemplateFormModal = ({
  opened,
  onClose,
  onSuccess,
  template = null, // null = создание, object = редактирование
}) => {
  const isEditing = !!template;

  const [formData, setFormData] = useState({
    channels: [],
    message_text: '',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Инициализация формы при открытии
  useEffect(() => {
    if (opened) {
      if (template) {
        setFormData({
          channels: template.channels || [],
          message_text: template.message_text || '',
        });
      } else {
        setFormData({
          channels: [],
          message_text: '',
        });
      }
      setError(null);
    }
  }, [opened, template]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Валидация
    if (!formData.channels || formData.channels.length === 0) {
      setError('Выберите хотя бы один канал');
      return;
    }

    if (!formData.message_text || !formData.message_text.trim()) {
      setError('Введите текст сообщения');
      return;
    }

    setLoading(true);
    try {
      const data = {
        channels: formData.channels,
        message_text: formData.message_text.trim(),
      };

      if (isEditing) {
        await api.updateTemplate(template.id, data);
      } else {
        await api.createTemplate(data);
      }

      onSuccess();
    } catch (err) {
      setError(err.message || 'Ошибка при сохранении шаблона');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditing ? 'Редактировать шаблон' : 'Новый шаблон сообщения'}
      size="lg"
      radius="lg"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error}
            </Alert>
          )}

          {/* Каналы */}
          <MultiSelect
            label="Каналы"
            placeholder="Выберите каналы"
            required
            data={CHANNEL_OPTIONS}
            value={formData.channels}
            onChange={(value) => setFormData({ ...formData, channels: value })}
            searchable
            nothingFoundMessage="Каналы не найдены"
            leftSection={<IconTemplate size={16} />}
          />

          {/* Текст сообщения */}
          <Textarea
            label="Текст сообщения"
            placeholder="Введите текст шаблона сообщения..."
            required
            autosize
            minRows={6}
            maxRows={12}
            value={formData.message_text}
            onChange={(e) => setFormData({ ...formData, message_text: e.target.value })}
          />

          {/* Кнопки */}
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose} disabled={loading}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              {isEditing ? 'Сохранить' : 'Создать'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default MessageTemplateFormModal;
